import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ClipboardCheck, Send, Sparkles } from 'lucide-react';
import { AsyncBoundary, Drawer, useToast } from '@kollez/design-system';
import { usePartnerColleges, useOpportunities, useOffers, usePartnerSkillDemand, usePartnerPipeline, useCompanyDashboard, type PartnerSkillDemand, type PartnerPipeline } from '@/lib/queries';
import { apiPost } from '@/lib/api';
import { useAuthStore } from '@/lib/authStore';
import { formatLPA, formatIndianDate } from '@kollez/shared';

/**
 * /company/campus-hiring (F87b) — direct port of kollez/pages/company/dashboard.html.
 *   Header + 4 KPIs (hires vs target / applications / offer-acceptance / awaiting-reply)
 *   Left: campus visits with .camp rows + .dt date tile + Upcoming/Invitations/Done segmented
 *   Right: funnel (Applied → Accepted) + offers expiring soon list
 */

type Seg = 'up' | 'inv' | 'done';

export function CompanyCampusHiringPage() {
  const colleges = usePartnerColleges();
  const opps = useOpportunities();
  const offers = useOffers();
  const partnerId = useAuthStore((s) => s.user?.partner);
  const skillDemandQ = usePartnerSkillDemand(partnerId, 10);
  const pipelineQ = usePartnerPipeline(partnerId);
  const dashboardQ = useCompanyDashboard();
  const dashKpis = dashboardQ.data?.kpis;
  const [seg, setSeg] = useState<Seg>('up');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteCollege, setInviteCollege] = useState('');
  const [inviteOpp, setInviteOpp] = useState('');
  const [inviteWindow, setInviteWindow] = useState('');
  const [inviteNote, setInviteNote] = useState("We'd like to run the online test first, then a one-day interview visit.");
  const [inviteSending, setInviteSending] = useState(false);
  const toast = useToast();

  const collegeRows = colleges.data?.colleges ?? [];
  const oppRows = opps.data?.opportunities ?? [];
  const offerRows = offers.data?.offers ?? [];

  // Deep-audit note (Page 42/N): dropped the hardcoded 'target = 600'
  // local. The partner org has no target field today; the 'Hires against
  // target' KPI has been renamed to just 'Accepted offers'.
  const hires = offerRows.filter((o) => /accept/i.test(o.status)).length;
  const totalOffers = offerRows.length;
  const awaiting = offerRows.filter((o) => o.status === 'released').length;
  const acceptance = totalOffers > 0 ? Math.round((hires / totalOffers) * 100) : 0;

  // Deep-audit fix (Page 42/N): the funnel used to include Assessment
  // (applied * 0.45) and Interview (applied * 0.17) rows computed from
  // invented conversion rates — a company would look at 'Interview: 340'
  // and act on a number that had no source. No API surfaces per-stage
  // counts across the partner's opportunities today. Trimmed to the two
  // stages we actually have real data for (Applied, Offered) plus the
  // subset we know (Accepted). Wire the full funnel once /company/funnel
  // or an aggregated /opportunities/funnel endpoint lands.
  const funnel = useMemo(() => {
    const applied = offerRows.length + oppRows.reduce((sum, o) => sum + ((o as { statistics?: { applications?: number } }).statistics?.applications ?? 0), 0);
    return [
      ['Applied', applied],
      ['Offered', totalOffers],
      ['Accepted', hires],
    ] as const;
  }, [offerRows, oppRows, totalOffers, hires]);

  const camps = useMemo(() => {
    return collegeRows.map((r) => ({
      id: r._id,
      name: r.name,
      city: r.type,
      detail: `${r.offersReleased ?? 0} offers · ${r.offersAccepted ?? 0} accepted${typeof r.totalStudents === 'number' ? ` · ${r.totalStudents} students` : ''}`,
      day: null as number | null,
      mon: r.daysToMouExpiry !== null && r.daysToMouExpiry !== undefined && r.daysToMouExpiry < 60 ? 'Renew' : '—',
      state: r.mouActive ? (r.offersReleased > 0 ? 'up' : 'inv') : 'inv',
    }));
  }, [collegeRows]);

  const filteredCamps = camps.filter((c) => c.state === seg);
  const expiring = offerRows.filter((o) => o.deadline && new Date(o.deadline).getTime() - Date.now() < 7 * 24 * 3600 * 1000 && o.status === 'released').slice(0, 5);

  return (
    <div className="stack">
      <style>{`
        .camp { display: grid; grid-template-columns: 56px minmax(0,1fr) auto; gap: 14px; align-items: center; padding: 14px 20px; border-bottom: 1px solid rgb(var(--line-tertiary)); }
        .camp:last-child { border-bottom: 0; }
        @media (max-width: 639px) { .camp { grid-template-columns: 48px minmax(0,1fr); } .camp > .c-act { grid-column: 2; justify-self: start; } }
        .dt { text-align: center; border: 1px solid rgb(var(--line-secondary)); border-radius: 8px; overflow: hidden; }
        .dt span { display: block; font-size: .625rem; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; background: rgb(var(--bg-inkwell)); color: #fff; padding: 2px 0; }
        .dt b { display: block; font-family: "Newsreader", Georgia, serif; font-weight: 500; font-size: 1.375rem; line-height: 1.4; }
        .fun { display: grid; gap: 10px; }
        .fun div { display: grid; grid-template-columns: 110px minmax(0,1fr) 56px; gap: 10px; align-items: center; font-size: .875rem; }
        .fun .bar { height: 10px; border-radius: 3px; }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          {/* Deep-audit fix (Page 42/N): eyebrow used to read
              'Season · target N hires' with N hardcoded to 600. The
              partner org has no target field on it today. Trimmed. */}
          <p className="eyebrow">Season</p>
          <h1 className="h-page">Campus hiring</h1>
          <p className="subhead">
            {collegeRows.length} campuses this season.{' '}
            {awaiting > 0 ? `${awaiting} candidates haven't replied to offers.` : 'All offers replied to.'}
          </p>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-outline" to="/assess/exams"><ClipboardCheck size={14} />Screening tests</Link>
          <button className="btn btn-primary" onClick={() => setInviteOpen(true)}><Plus size={14} />Invite to campus</button>
        </div>
      </div>

      {/* Server-authoritative KPIs from /company/dashboard (Pass 14 composite):
          active openings distribution, applications last 7d, upcoming
          interviews, offers pending, accepted-CTC committed. */}
      {dashKpis && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <div className="card kpi">
            <span className="kpi-label">Active openings</span>
            <span className="kpi-value">{dashKpis.openingsByStatus['Active'] ?? 0}</span>
            <span className="kpi-meta">
              {Object.values(dashKpis.openingsByStatus).reduce((n, v) => n + v, 0)} total
            </span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Applications · 7d</span>
            <span className="kpi-value">{dashKpis.applicationsLast7d}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Interviews · this week</span>
            <span className="kpi-value">{dashKpis.upcomingInterviewsCount}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Committed CTC</span>
            <span className="kpi-value">
              {dashKpis.fundedByAcceptedCTC > 0 ? formatLPA(dashKpis.fundedByAcceptedCTC) : '—'}
            </span>
            <span className="kpi-meta">
              {dashKpis.offersAcceptedLifetime} accepted · {dashKpis.activeMous} MoUs
            </span>
          </div>
        </div>
      )}

      {/* Deep-audit fix (Page 42/N): the 'Hires against target' KPI used to
          divide by a hardcoded target of 600 — a made-up denominator would
          show a company like 'You're at 8% of your target' when no target
          existed. Renamed to 'Accepted offers' and dropped the bar/pct
          against the fictional denominator. */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <div className="card kpi">
          <span className="kpi-label">Accepted offers</span>
          <span className="kpi-value">{hires}</span>
          <span className="kpi-meta">this season</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Applications</span>
          <span className="kpi-value">{funnel[0][1].toLocaleString('en-IN')}</span>
          <span className="kpi-meta">across {collegeRows.length} campuses</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Offer acceptance</span>
          <span className="kpi-value">{acceptance}%</span>
          <span className="kpi-meta">{hires} of {totalOffers} replied</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Awaiting reply</span>
          <span className="kpi-value">{awaiting}</span>
          <span className="kpi-meta">{expiring.length} expire this week</span>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] items-start">
        <section className="card">
          <div className="card-head">
            <h2 className="h-section">Campus visits</h2>
            <div className="segmented">
              <button aria-pressed={seg === 'up'} onClick={() => setSeg('up')}>Upcoming</button>
              <button aria-pressed={seg === 'inv'} onClick={() => setSeg('inv')}>
                Invitations {camps.filter((c) => c.state === 'inv').length > 0 && <span className="badge badge-warning ml-1">{camps.filter((c) => c.state === 'inv').length}</span>}
              </button>
              <button aria-pressed={seg === 'done'} onClick={() => setSeg('done')}>Done</button>
            </div>
          </div>
          <AsyncBoundary
            kind="table"
            inCard
            loading={colleges.isLoading}
            error={colleges.error}
            empty={!colleges.isLoading && filteredCamps.length === 0}
            emptyTitle="No campus visits yet"
            emptyHint="Invite a partner college. Their placement officer confirms a date, and eligible students are notified automatically."
            onRetry={() => void colleges.refetch()}
          >
            <div>
              {filteredCamps.map((c) => (
                <div key={c.id} className="camp">
                  <div className="dt">
                    <span>{c.mon}</span>
                    <b>{c.day ?? '—'}</b>
                  </div>
                  <div className="min-w-0">
                    <div className="cell-title">{c.name}, {c.city}</div>
                    <div className="cell-sub">{c.detail}</div>
                  </div>
                  {c.state === 'inv' ? (
                    /* Deep-audit fix (Page 42/N): Decline/Accept
                       previously fired lying toasts with no API call.
                       Disabled with 'soon' — campus-invitation
                       accept/decline ships in the next release. */
                    <div className="c-act flex gap-2">
                      <button className="btn btn-outline btn-sm" disabled title="Coming soon">Decline · soon</button>
                      <button className="btn btn-primary btn-sm" disabled title="Coming soon">Accept · soon</button>
                    </div>
                  ) : (
                    <Link className="c-act btn btn-outline btn-sm" to="/company/candidates">Open</Link>
                  )}
                </div>
              ))}
            </div>
          </AsyncBoundary>
        </section>

        <div className="grid gap-5 min-w-0">
          <RealFunnelCard data={pipelineQ.data ?? null} loading={pipelineQ.isLoading} fallback={funnel} />

          <SkillDemandCard
            data={skillDemandQ.data ?? null}
            loading={skillDemandQ.isLoading}
            enabled={Boolean(partnerId)}
          />

          <section className="card">
            <div className="card-head"><h2 className="h-section">Offers expiring soon</h2></div>
            <ul className="m-0 p-0 list-none">
              {expiring.length === 0 && <li className="p-4 text-ink-tertiary text-sm">No offers expiring in the next 7 days.</li>}
              {expiring.map((o) => (
                <li key={o._id} className="flex items-center gap-3 px-5 py-3 border-b border-line-tertiary last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="cell-title">{(o as { candidateName?: string }).candidateName ?? '—'}</div>
                    <div className="cell-sub">{typeof o.ctc === 'number' ? formatLPA(o.ctc) : '—'}</div>
                  </div>
                  <span className="badge badge-warning">{o.deadline ? formatIndianDate(o.deadline) : '—'}</span>
                </li>
              ))}
            </ul>
            {/* Deep-audit fix (Page 42/N): the 'Remind all N' button fired
                a lying toast — 'Reminders sent to N candidates' — with no
                API call. Bulk deadline reminders ship with the notification
                job for offers-nearing-expiry; disabled here for now. */}
            {expiring.length > 0 && (
              <div className="card-foot">
                <button className="btn btn-outline btn-sm w-full" disabled title="Bulk deadline reminders ship with the offer-expiry job in the next release.">
                  <Send size={14} />Remind all {expiring.length} · soon
                </button>
              </div>
            )}
          </section>

          <Link to="/nexie" className="ai-suggest">
            <Sparkles size={14} />
            <div><b>Ask Nexie</b> — "Which campus gave us the fastest offer acceptance?"</div>
          </Link>
        </div>
      </div>

      <Drawer open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite to campus" size="lg">
        <label className="grid gap-1.5 mb-4">
          <span className="label">College</span>
          <select className="select" value={inviteCollege} onChange={(e) => setInviteCollege(e.target.value)}>
            <option value="">Pick a partner college…</option>
            {collegeRows.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </label>
        <label className="grid gap-1.5 mb-4">
          <span className="label">Opening (optional)</span>
          <select className="select" value={inviteOpp} onChange={(e) => setInviteOpp(e.target.value)}>
            <option value="">No specific role — we'll share the details later</option>
            {oppRows.map((o) => <option key={o._id} value={o._id}>{o.title}</option>)}
          </select>
        </label>
        <label className="grid gap-1.5 mb-4">
          <span className="label">Preferred window</span>
          <input className="input" placeholder="e.g. 13 – 24 Oct 2026" value={inviteWindow} onChange={(e) => setInviteWindow(e.target.value)} />
        </label>
        <label className="grid gap-1.5 mb-4">
          <span className="label">Note to placement officers (min 10 chars)</span>
          <textarea className="textarea" rows={3} value={inviteNote} onChange={(e) => setInviteNote(e.target.value)} />
        </label>
        <div className="drawer-foot">
          <button className="btn btn-outline" onClick={() => setInviteOpen(false)}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!inviteCollege || inviteNote.trim().length < 10 || inviteSending}
            onClick={async () => {
              setInviteSending(true);
              try {
                await apiPost('/campus-invitations', {
                  college: inviteCollege,
                  opportunity: inviteOpp || undefined,
                  preferredWindow: inviteWindow.trim() ? { label: inviteWindow.trim() } : undefined,
                  note: inviteNote.trim(),
                });
                toast.show({ message: 'Invitation sent. Placement officers of that college are notified.' });
                setInviteOpen(false);
                setInviteCollege(''); setInviteOpp(''); setInviteWindow('');
              } catch (e) {
                toast.show({ tone: 'warning', message: (e as Error).message });
              } finally {
                setInviteSending(false);
              }
            }}
          >
            <Send size={14} />{inviteSending ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </Drawer>
    </div>
  );
}

/**
 * Real hiring funnel — reads /partners/:id/pipeline (Pass 3 relationship endpoint).
 * Buckets Application.status into the 6 canonical stages (Applied → Under review
 * → Shortlisted → Interview → Offered → Accepted) so the bar heights are honest
 * counts pulled from the same source of truth as the Kanban. Falls back to the
 * pre-existing 3-bar approximation if the endpoint hasn't returned yet.
 */
function RealFunnelCard({
  data,
  loading,
  fallback,
}: {
  data: PartnerPipeline | null;
  loading: boolean;
  fallback: ReadonlyArray<readonly [string, number]>;
}) {
  const apps = data?.applicationsByStatus ?? {};
  const offers = data?.offersByStatus ?? {};

  // Bucket every application status into the 6 canonical funnel stages.
  const sumStatuses = (keys: string[], src: Record<string, number>) =>
    keys.reduce((n, k) => n + (src[k] ?? 0), 0);

  const applied = sumStatuses(
    ['Applied', 'Under_Review', 'Shortlisted', 'Assessment_Pending', 'Assessment_Submitted', 'Interview_Scheduled', 'Interview_Completed', 'Offered', 'Accepted', 'On_Hold'],
    apps,
  );
  const shortlisted = sumStatuses(
    ['Shortlisted', 'Assessment_Pending', 'Assessment_Submitted', 'Interview_Scheduled', 'Interview_Completed', 'Offered', 'Accepted'],
    apps,
  );
  const interview = sumStatuses(['Interview_Scheduled', 'Interview_Completed', 'Offered', 'Accepted'], apps);
  const offered = sumStatuses(['uploaded', 'ready', 'released', 'opened', 'accepted', 'accepted_final', 'declined', 'undone'], offers);
  const accepted = sumStatuses(['accepted', 'accepted_final'], offers);

  const rows: Array<[string, number]> = data
    ? [
        ['Applied', applied],
        ['Shortlisted', shortlisted],
        ['Interview', interview],
        ['Offered', offered],
        ['Accepted', accepted],
      ]
    : fallback.map(([l, n]) => [l, n]);

  const top = Math.max(1, ...rows.map(([, n]) => n));

  return (
    <section className="card card-pad grid gap-4">
      <div className="flex items-baseline justify-between">
        <h2 className="h-section">Funnel, all campuses</h2>
        {loading && <span className="cell-sub">Loading…</span>}
        {data && data.medianCTCInr && <span className="cell-sub">Median CTC: {formatLPA(data.medianCTCInr)}</span>}
      </div>
      <div className="fun">
        {rows.map(([label, n]) => (
          <div key={label}>
            <span>{label}</span>
            <div className="bar"><span style={{ width: `${Math.min(100, (n / top) * 100)}%` }} /></div>
            <span className="text-right">{n.toLocaleString('en-IN')}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Ship 5f/5b — skill demand across the partner's own opportunities.
 * Sourced from GET /partners/:id/skill-demand (Pass 10 relationship endpoint).
 * Rendered as two ranked lists (required vs preferred) with a bar meter
 * relative to the top skill's count, so the eye reads relative demand
 * without needing exact counts.
 */
function SkillDemandCard({
  data,
  loading,
  enabled,
}: {
  data: PartnerSkillDemand | null;
  loading: boolean;
  enabled: boolean;
}) {
  if (!enabled) return null;
  const required = data?.requiredSkills ?? [];
  const preferred = data?.preferredSkills ?? [];
  const maxReq = required[0]?.count ?? 1;
  const maxPref = preferred[0]?.count ?? 1;
  return (
    <section className="card card-pad grid gap-4">
      <div>
        <h2 className="h-section">Your skill demand</h2>
        <p className="cell-sub m-0">
          Across your {data?.totalOpportunities ?? 0} opportunities. Ranked by how many openings
          ask for each canonical skill.
        </p>
      </div>
      {loading && <p className="cell-sub m-0">Loading…</p>}
      {!loading && required.length === 0 && preferred.length === 0 && (
        <p className="cell-sub m-0">
          No skills listed on any opportunity yet. Add required skills to each opening so
          candidates and TPOs can see fit at a glance.
        </p>
      )}
      {required.length > 0 && (
        <div>
          <div className="cell-sub mb-1">Required</div>
          <ol className="stack">
            {required.slice(0, 8).map((r) => (
              <li key={r.skill} className="grid grid-cols-[minmax(0,1fr)_60px_36px] items-center gap-2">
                <span className="truncate">
                  <Link to={`/skills-catalog/${encodeURIComponent(r.skill)}`} className="link">
                    {r.skill}
                  </Link>
                </span>
                <span className="bar"><span style={{ width: `${Math.max(6, Math.round((r.count / maxReq) * 100))}%` }} /></span>
                <span className="text-right text-sm">{r.count}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
      {preferred.length > 0 && (
        <div>
          <div className="cell-sub mb-1">Preferred</div>
          <ol className="stack">
            {preferred.slice(0, 8).map((r) => (
              <li key={r.skill} className="grid grid-cols-[minmax(0,1fr)_60px_36px] items-center gap-2">
                <span className="truncate">
                  <Link to={`/skills-catalog/${encodeURIComponent(r.skill)}`} className="link">
                    {r.skill}
                  </Link>
                </span>
                <span className="bar"><span style={{ width: `${Math.max(6, Math.round((r.count / maxPref) * 100))}%` }} /></span>
                <span className="text-right text-sm">{r.count}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}
